import React, { useState } from "react";
import { Vehicle } from "../../graphql/generated/graphql";
import { formatPrice } from "../../utils/formatters";
import Link from "next/link";
import { CREDIQ_PRE_APROV } from "../../utils/constants";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";

const getLoanFields = (
  price: number,
  customDownPayment?: number,
  term?: number
) => {
  const downPayment = customDownPayment || price * 0.25;
  const monthlyInterestRate = 0.012; // ASK their interest rate
  return {
    downPayment,
    term,
    monthlyFee:
      ((price - downPayment) *
        monthlyInterestRate *
        Math.pow(1 + monthlyInterestRate, term)) /
      (Math.pow(1 + monthlyInterestRate, term) - 1),
  };
};

const LoanCalculator = ({
  vehicle,
  financingData,
}: {
  vehicle: Vehicle;
  financingData: { downPayment: number; term: number };
}) => {
  const { price, currency } = vehicle;
  const { downPayment, term } = financingData;
  const downPaymentAmount = price * downPayment;
  const [customDownPayment, setCustomDownPayment] = useState(downPaymentAmount);
  const { monthlyFee } = getLoanFields(price, customDownPayment, term);

  const changeCustomDownpayment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const downPayment = Number(e.target.value);
    if (downPayment > price * 0.75) return;
    setCustomDownPayment(downPayment);
  };

  return (
    <div className="mt-3 w-full p-5 bg-white shadow-md rounded-lg border border-gray-200">
      <div className="text-center">
        <p className="text-sm text-gray-700">Calculá tu cuota aquí</p>
      </div>
      <div className="mt-4 space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-600">Precio del vehículo:</span>
          <span className="font-semibold">{formatPrice(price, currency)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Prima mínima:</span>
          <span className="font-semibold">
            {/* <span className="font-normal text-gray-700">
              ({downPayment * 100}%)
            </span>{" "} */}
            {formatPrice(customDownPayment, currency)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Financiamiento:</span>
          <span className="font-semibold">
            {formatPrice(price - customDownPayment, currency)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Plazo:</span>
          <span className="font-semibold">{term} meses</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Cuota mensual:</span>
          <span className="font-semibold text-sky-700">
            {formatPrice(monthlyFee, currency)}
          </span>
        </div>
      </div>
      <div className="mt-4">
        <label htmlFor="downPayment" className="block text-gray-500 text-sm">
          Calcular con otra prima
        </label>
        <input
          type="number"
          id="downPayment"
          className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-sky-700 focus:outline-none"
          placeholder="Ingresar monto"
          value={customDownPayment}
          max={price * 0.75}
          onChange={changeCustomDownpayment}
        />
      </div>
      <Link href={CREDIQ_PRE_APROV} target="_blank">
        <button className="mt-4 w-full bg-sky-700 text-white font-semibold py-2 rounded-md hover:bg-sky-600 transition">
          Solicitar Pre Aprobación
        </button>
      </Link>
      <p className="mt-2 text-xs text-gray-400 text-center">
        *Este monto puede variar luego del estudio crediticio
      </p>
      <div className="mt-2 flex justify-center">
        <Link href={`/crediq`}>
          <span className="text-cyan-500 hover:text-cyan-400 flex gap-x-1 items-center cursor-pointer font-medium">
            Ver más autos financiables con CrediQ
            <ArrowTopRightOnSquareIcon className="h-4 w-4 " />
          </span>
        </Link>
      </div>
    </div>
  );
};

export default LoanCalculator;
